'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["tableWidth", "width", "onChange", "onStep"];
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Fieldset } from 'foundations-components/transitional/components/Fieldset';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import { H5 } from 'foundations-components/transitional/components/H5';
import { Select } from 'foundations-components/transitional/components/Select';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import styled from 'foundations-theming/utils';
import PropTypes from 'prop-types';
import { TableBorderStyles } from '../../constants/table';
import { textButtonStyles, toolbarI18nKey } from '../../constants/toolbar';
import { useDebouncedOnChange } from '../../hooks/useDebouncedOnChange';
import { isPercentage, percentageStringToNumber, pixelStringToNumber } from '../../utils/table';
import { PercentageStepperInput, PixelStepperInput } from '../steppers';
import StyleProtectedUIDropdown from '../style-protected/StyleProtectedUIDropdown';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const makeEventCompatible = fn => evt => {
  const {
    value
  } = evt.target;
  fn(value);
};
const CellWidthStepperInput = _ref => {
  let {
      tableWidth,
      width,
      onChange,
      onStep
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (isPercentage(tableWidth)) {
    return /*#__PURE__*/_jsx(PercentageStepperInput, Object.assign({}, rest, {
      min: 2,
      max: 100,
      value: percentageStringToNumber(width),
      onChange: onChange,
      onStep: onStep
    }));
  } else {
    return /*#__PURE__*/_jsx(PixelStepperInput, Object.assign({}, rest, {
      min: 2,
      value: pixelStringToNumber(width),
      onChange: onChange,
      onStep: onStep
    }));
  }
};
const propTypes = {
  className: PropTypes.string.isRequired,
  dropdownClassName: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  tableWidth: PropTypes.string.isRequired,
  dimensions: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number
  }),
  onDimensionChange: PropTypes.func.isRequired,
  onBorderWidthChange: PropTypes.func.isRequired
};
function TableCellStyleControlButton(props) {
  const {
    className,
    dropdownClassName,
    disabled,
    borderWidth,
    borderStyle,
    tableWidth,
    dimensions,
    onDimensionChange,
    onBorderWidthChange,
    onBorderStyleChange
  } = props;
  const _handleDimensionChange = (dimensionType, evt) => {
    const {
      value
    } = evt.target;
    onDimensionChange({
      dimensions: Object.assign({}, dimensions, {
        [dimensionType]: value
      }),
      dimensionType
    });
  };
  const [handleBorderWidthChange, handleBorderWidthStep] = useDebouncedOnChange(onBorderWidthChange);
  const [handleDimensionChange, handleDimensionStep] = useDebouncedOnChange(_handleDimensionChange);
  return /*#__PURE__*/_jsx(StyleProtectedUIDropdown, {
    "data-test-id": "table-cell-style-control-button",
    use: "unstyled",
    buttonText: I18n.text(toolbarI18nKey('tableCellStyle')),
    buttonUse: "transparent",
    buttonClassName: className,
    dropdownClassName: dropdownClassName,
    autoPlacement: "horiz",
    placement: "bottom right",
    closeOnMenuClick: false,
    animateOnToggle: false,
    disabled: disabled,
    arrowSize: "medium",
    children: /*#__PURE__*/_jsxs(Fieldset, {
      "data-test-id": "table-cell-style-control-content",
      margin: "400",
      minInlineSize: "250px",
      maxInlineSize: "300px",
      _size: "small",
      children: [/*#__PURE__*/_jsx(H5, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "RichTextLib.table.toolbar.cell.title"
        })
      }), /*#__PURE__*/_jsxs(TFlex, {
        alignment: "center",
        justify: "between",
        marginBlockStart: "400",
        children: [/*#__PURE__*/_jsx(FormLabel, {
          paddingBlock: "100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "RichTextLib.table.toolbar.borderWidth"
          })
        }), /*#__PURE__*/_jsx(PixelStepperInput, {
          "data-test-id": "cell-border-width-input",
          min: 0,
          max: 15,
          inlineSize: "144px",
          value: pixelStringToNumber(borderWidth),
          onChange: makeEventCompatible(handleBorderWidthChange),
          onStep: makeEventCompatible(handleBorderWidthStep)
        })]
      }), /*#__PURE__*/_jsxs(TFlex, {
        alignment: "center",
        justify: "between",
        marginBlockStart: "400",
        children: [/*#__PURE__*/_jsx(FormLabel, {
          paddingBlock: "100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "RichTextLib.table.toolbar.borderStyle"
          })
        }), /*#__PURE__*/_jsx(Select, {
          "data-test-id": "cell-border-style-select",
          inlineSize: "144px",
          options: TableBorderStyles.map(style => ({
            value: style,
            text: I18n.text(`RichTextLib.table.toolbar.borderStyleOptions.${style}`)
          })),
          value: borderStyle,
          onChange: makeEventCompatible(onBorderStyleChange)
        })]
      }), /*#__PURE__*/_jsxs(TFlex, {
        alignment: "center",
        justify: "between",
        marginBlockStart: "400",
        children: [/*#__PURE__*/_jsx(FormLabel, {
          paddingBlock: "100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "RichTextLib.table.toolbar.cell.width"
          })
        }), /*#__PURE__*/_jsx(CellWidthStepperInput, {
          "data-test-id": "cell-width-input",
          inlineSize: "144px",
          tableWidth: tableWidth,
          width: dimensions.width,
          onChange: handleDimensionChange.bind(null, 'width'),
          onStep: handleDimensionStep.bind(null, 'width')
        })]
      }), /*#__PURE__*/_jsxs(TFlex, {
        alignment: "center",
        justify: "between",
        marginBlockStart: "400",
        children: [/*#__PURE__*/_jsx(FormLabel, {
          paddingBlock: "100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "RichTextLib.table.toolbar.cell.height"
          })
        }), /*#__PURE__*/_jsx(PixelStepperInput, {
          "data-test-id": "cell-height-input",
          min: 2,
          inlineSize: "144px",
          value: pixelStringToNumber(dimensions.height),
          onChange: handleDimensionChange.bind(null, 'height'),
          onStep: handleDimensionStep.bind(null, 'height')
        })]
      })]
    })
  });
}
TableCellStyleControlButton.propTypes = propTypes;
TableCellStyleControlButton.displayName = 'TableCellStyleControlButton';
const StyledTableCellStyleControlButton = styled(TableCellStyleControlButton).withConfig({
  displayName: "TableCellStyleControlButton__StyledTableCellStyleControlButton",
  componentId: "sc-12x89yq-0"
})(["", ""], textButtonStyles);
export default StyledTableCellStyleControlButton;